import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  fetchProperties,
  deleteProperty,
} from "../../lib/propertiesApi";
import AdminRoute from "../../components/AdminRoute";
import { Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, PlusCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const SORT_KEYS = {
  name: "name",
  city: "city",
  location: "location",
  price: "priceValue",
  status: "status",
  created: "createdAt",
};

function AdminDashboardInner() {
  const queryClient = useQueryClient();
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");

  const { data: properties = [], isLoading, error } = useQuery({
    queryKey: ["admin-properties"],
    queryFn: async () => {
      const { data, error } = await fetchProperties();
      if (error) throw error;
      return data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProperty,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success("Property removed");
    },
    onError: (err) => toast.error(err.message || "Delete failed"),
  });

  const sortedList = useMemo(() => {
    const key = SORT_KEYS[sortKey] || sortKey;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...properties].sort((a, b) => {
      let va = a[key];
      let vb = b[key];
      if (key === "priceValue") {
        va = Number(va) || 0;
        vb = Number(vb) || 0;
        return (va - vb) * dir;
      }
      if (key === "createdAt") {
        va = a.createdAt || "";
        vb = b.createdAt || "";
      }
      const sa = String(va ?? "").toLowerCase();
      const sb = String(vb ?? "").toLowerCase();
      return (sa < sb ? -1 : sa > sb ? 1 : 0) * dir;
    });
  }, [properties, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else setSortKey(key);
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    return sortDir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  if (error) {
    return (
      <Card className="border-destructive/50">
        <CardContent className="pt-6">
          <p className="text-destructive">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-accent/10 to-primary/5 border-b border-border px-6 py-5 flex flex-row items-center justify-between">
        <h1 className="text-2xl font-bold text-primary">Properties</h1>
        <Link to="/admin/properties/new">
          <Button className="bg-accent text-primary hover:bg-accent/90 font-medium gap-2 shadow-md">
            <PlusCircle className="h-4 w-4" />
            Add property
          </Button>
        </Link>
      </div>
      <div className="p-6">
        {isLoading ? (
          <p className="text-muted-foreground py-12 text-center">Loading…</p>
        ) : sortedList.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center">No properties yet. Add one to get started.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  {["name", "city", "location", "price", "status"].map((col) => (
                    <th
                      key={col}
                      className="text-left py-4 px-4 font-semibold text-foreground cursor-pointer hover:bg-muted/70 transition-colors flex items-center gap-1"
                      onClick={() => toggleSort(col)}
                    >
                      {col.charAt(0).toUpperCase() + col.slice(1)}
                      <SortIcon column={col} />
                    </th>
                  ))}
                  <th className="text-right py-4 px-4 font-semibold text-foreground w-40">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedList.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 hover:bg-accent/5 transition-colors">
                    <td className="py-4 px-4 font-medium text-primary">{p.name}</td>
                    <td className="py-4 px-4">{p.city}</td>
                    <td className="py-4 px-4">{p.location}</td>
                    <td className="py-4 px-4 font-medium">{p.price}</td>
                    <td className="py-4 px-4">
                      <Badge
                        variant={p.status === "Sold" ? "secondary" : "default"}
                        className={p.status === "Conditionally Sold" ? "bg-amber-500/20 text-amber-800 dark:text-amber-200" : ""}
                      >
                        {p.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/admin/properties/${p.id}/edit`}>
                          <Button variant="outline" size="sm" className="gap-1 border-primary/30">
                            <Pencil className="h-3 w-3" />
                            Edit
                          </Button>
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10">
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete property?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove &quot;{p.name}&quot; ({p.location}). This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => deleteMutation.mutate(p.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <AdminRoute>
      <AdminDashboardInner />
    </AdminRoute>
  );
}
